import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle2, ShieldCheck, Loader2, CreditCard, Landmark, Check, X, BadgeCheck, Maximize2, Download, Copy, Zap, ExternalLink } from "lucide-react";
import { get, post } from "../lib/api";
import { Seo } from "../lib/seo";
import { APP_URL } from "../data/nav";

const peso = (v) => "\u20b1" + Number(v || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

let _capPromise = null;
function loadCapture(clientId) {
  if (window.ppLinkCap) return Promise.resolve(window.ppLinkCap);
  if (_capPromise) return _capPromise;
  _capPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=PHP&intent=capture&components=buttons`;
    s.setAttribute("data-namespace", "ppLinkCap");
    s.onload = () => resolve(window.ppLinkCap);
    s.onerror = () => { _capPromise = null; reject(new Error("PayPal failed to load")); };
    document.body.appendChild(s);
  });
  return _capPromise;
}
let _subPromise = null;
function loadSub(clientId) {
  if (window.ppLinkSub) return Promise.resolve(window.ppLinkSub);
  if (_subPromise) return _subPromise;
  _subPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=PHP&vault=true&intent=subscription&components=buttons`;
    s.setAttribute("data-namespace", "ppLinkSub");
    s.onload = () => resolve(window.ppLinkSub);
    s.onerror = () => { _subPromise = null; reject(new Error("PayPal failed to load")); };
    document.body.appendChild(s);
  });
  return _subPromise;
}

function Centered({ children }) {
  return <div className="min-h-screen bg-surface-sunk grid place-items-center p-6">{children}</div>;
}

export default function PayLink() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    get(`/pay/${token}`).then(setData).catch((e) => setErr(e?.response?.data?.detail || "This payment link is invalid or has expired."));
  }, [token]);

  if (err) return <Centered><div className="text-center max-w-md" data-testid="paylink-error">
    <div className="h-12 w-12 rounded-full bg-signal/10 grid place-items-center mx-auto mb-4"><ShieldCheck className="h-6 w-6 text-signal" /></div>
    <h1 className="text-2xl text-ink-950">Link unavailable</h1>
    <p className="mt-2 text-ink-600">{err}</p>
  </div></Centered>;

  if (!data) return <Centered><Loader2 className="h-6 w-6 animate-spin text-brand-700" /></Centered>;

  if (done && data.is_credits_topup && !done.pending) return <Centered><div className="text-center max-w-md" data-testid="paylink-credit-success">
    <div className="h-14 w-14 rounded-full bg-positive/10 grid place-items-center mx-auto mb-4"><Zap className="h-7 w-7 text-positive" /></div>
    <h1 className="text-2xl text-ink-950">Credits added!</h1>
    <p className="mt-2 text-ink-600">Your top-up was successful.</p>
    <div className="mt-5 mx-auto max-w-xs rounded-xl border border-line bg-white p-5 text-left">
      <div className="flex justify-between py-1.5 text-sm"><span className="text-ink-500">Amount paid</span><span className="font-mono text-ink-900" data-testid="paylink-credit-amount">{peso(done.amount)}</span></div>
      <div className="flex justify-between py-1.5 text-sm border-t border-line/60"><span className="text-ink-500">Credits added</span><span className="font-mono text-brand-700 font-semibold" data-testid="paylink-credit-count">+{done.credits || data.credits} credits</span></div>
    </div>
    <a href={APP_URL} target="_blank" rel="noreferrer" className="btn-primary btn-md mt-6 inline-flex items-center gap-2" data-testid="paylink-credit-goapp">Go to the app <ExternalLink className="h-4 w-4" /></a>
  </div></Centered>;

  if (done) return <Centered><div className="text-center max-w-md" data-testid="paylink-thankyou">
    <div className="h-12 w-12 rounded-full bg-positive/10 grid place-items-center mx-auto mb-4"><CheckCircle2 className="h-6 w-6 text-positive" /></div>
    <h1 className="text-2xl text-ink-950">Payment received</h1>
    <p className="mt-2 text-ink-600">Thank you! We've recorded your payment for <b>{data.package_label}</b>.
      {done.pending ? " We'll confirm your bank transfer shortly." : (done.provisioned ? " Your account is being set up." : " Our team will be in touch shortly.")}</p>
  </div></Centered>;

  if (data.already_paid) return <Centered><div className="text-center max-w-md" data-testid="paylink-paid">
    <div className="h-12 w-12 rounded-full bg-positive/10 grid place-items-center mx-auto mb-4"><CheckCircle2 className="h-6 w-6 text-positive" /></div>
    <h1 className="text-2xl text-ink-950">Already paid</h1>
    <p className="mt-2 text-ink-600">This payment link has already been completed. Thank you!</p>
  </div></Centered>;

  return (
    <>
      <Seo title="Secure payment" path={`/pay/${token}`} description="Complete your BloomCONNECT payment." noindex />
      <div className="min-h-screen bg-surface-sunk py-10 px-4">
        <div className="max-w-lg mx-auto" data-testid="paylink-page">
          <div className="flex items-center gap-2 text-brand-700 justify-center">
            <ShieldCheck className="h-5 w-5" /><span className="eyebrow">BloomCONNECT · Secure payment</span>
          </div>
          <div className="mt-4 card-hairline bg-white p-6 sm:p-8">
            <div className="text-2xs uppercase tracking-eyebrow text-ink-400">Payment for</div>
            <h1 className="mt-1 text-2xl text-ink-950" data-testid="paylink-label">{data.package_label}</h1>
            {data.customer_name && <p className="mt-1 text-sm text-ink-500">Billed to {data.customer_name}</p>}
            {data.credits ? <p className="mt-1 text-sm text-brand-700">Includes {data.credits} credits{data.is_credits_topup ? " added to your account" : ""}.</p> : null}

            {!data.paypal.configured ? (
              <div className="mt-5 text-sm text-signal" data-testid="paylink-not-configured">Payments aren't configured yet. Please contact us.</div>
            ) : (
              <Checkout data={data} token={token} onDone={setDone} />
            )}
          </div>
          <p className="mt-4 text-center text-2xs text-ink-400">Payments processed securely by PayPal · Bloom DevLabs Corp., Makati City</p>
        </div>
      </div>
    </>
  );
}

function Checkout({ data, token, onDone }) {
  const navigate = useNavigate();
  const recurring = data.recurring;
  const collect = data.collect_details;
  const isTopup = data.is_credits_topup;
  const [buyer, setBuyer] = useState({ name: data.customer_name || "", email: data.customer_email || "", business: "" });
  const [method, setMethod] = useState(recurring ? "card" : null);
  const [emailCheck, setEmailCheck] = useState({ state: isTopup ? "idle" : "ok" });
  const buyerRef = useRef(buyer);
  buyerRef.current = buyer;

  const emailToCheck = (collect ? buyer.email : data.customer_email) || "";
  useEffect(() => {
    if (!isTopup) { setEmailCheck({ state: "ok" }); return; }
    const e = emailToCheck.trim();
    if (!/.+@.+\..+/.test(e)) { setEmailCheck({ state: "idle" }); return; }
    setEmailCheck({ state: "loading" });
    const t = setTimeout(() => {
      get(`/pay/${token}/check-email?email=${encodeURIComponent(e)}`)
        .then((r) => setEmailCheck(r.registered ? { state: "ok", name: r.name, plan: r.plan_slug } : { state: "bad" }))
        .catch(() => setEmailCheck({ state: "idle" }));
    }, 450);
    return () => clearTimeout(t);
  }, [emailToCheck, isTopup, token]);

  const buyerReady = !collect || (/.+@.+\..+/.test(buyer.email) && buyer.name.trim().length > 1);
  const emailOk = !isTopup || emailCheck.state === "ok";
  const canChoose = buyerReady && emailOk;

  const isCard = method === "card";
  const [wantOr, setWantOr] = useState(!!data.needs_or);
  const eff = (!recurring && wantOr && data.or_option)
    ? { ...data, or_amount: data.or_option.or_amount, paypal_fee: data.or_option.paypal_fee,
        total_card: data.or_option.total_card, total_qr: data.or_option.total_qr }
    : (!recurring && !wantOr ? { ...data, or_amount: 0 } : data);
  const total = recurring ? data.total : (isCard ? eff.total_card : eff.total_qr);
  const buyerBody = () => ({ name: buyerRef.current.name, email: buyerRef.current.email || data.customer_email, business_name: buyerRef.current.business, needs_or: wantOr });
  const finish = (r) => {
    if (r.onboarding_token) {
      navigate("/welcome", { state: { token: r.onboarding_token, orderId: r.order_id, email: buyerRef.current.email || data.customer_email, summary: r.summary } });
    } else {
      onDone({ provisioned: r.provisioned, pending: !!r.needs_verification,
               amount: (r.summary && r.summary.amount) || total, credits: (r.summary && r.summary.credits) || data.credits });
    }
  };

  return (
    <div className="mt-6 space-y-5">
      {collect && (
        <div className="space-y-2" data-testid="paylink-details">
          <div className="eyebrow">Your details</div>
          <input className="field w-full" placeholder="Full name" value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} data-testid="paylink-name" />
          <input className="field w-full" placeholder="Email address" type="email" value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} data-testid="paylink-email" />
          <input className="field w-full" placeholder="Business name (optional)" value={buyer.business} onChange={(e) => setBuyer({ ...buyer, business: e.target.value })} data-testid="paylink-business" />
        </div>
      )}

      {isTopup && emailCheck.state === "loading" && (
        <p className="text-xs text-ink-400 flex items-center gap-1" data-testid="paylink-email-checking"><Loader2 className="h-3 w-3 animate-spin" /> Checking your account…</p>
      )}
      {isTopup && emailCheck.state === "ok" && (
        <div className="rounded-lg border border-positive/40 bg-positive/5 p-3 text-sm text-ink-700 flex items-center gap-2" data-testid="paylink-email-ok">
          <BadgeCheck className="h-4 w-4 text-positive shrink-0" /> Account found{emailCheck.name ? ` — credits will be added to ${emailCheck.name}` : ""}.
        </div>
      )}
      {isTopup && emailCheck.state === "bad" && (
        <div className="rounded-lg border border-negative/40 bg-negative/5 p-3 text-sm text-ink-700" data-testid="paylink-email-bad">
          <div className="flex items-center gap-2 font-medium text-negative"><X className="h-4 w-4 shrink-0" /> This email isn't a registered customer</div>
          <p className="mt-1 text-ink-600">Credit top-ups can only be added to an existing BloomCONNECT account. Please double-check the email, or contact us to get set up first.</p>
        </div>
      )}

      {!canChoose ? (
        <p className="text-sm text-ink-400" data-testid="paylink-gate-hint">
          {collect && !buyerReady ? "Enter your name and email above to continue." : "Enter a registered customer email above to continue."}
        </p>
      ) : (
        <>
          {!recurring && data.or_option && (
            <label className="flex items-start gap-2.5 rounded-lg border border-line p-3 text-sm cursor-pointer hover:border-brand-600/40" data-testid="paylink-or-toggle">
              <input type="checkbox" className="mt-0.5" checked={wantOr} onChange={(e) => setWantOr(e.target.checked)} data-testid="paylink-or-checkbox" />
              <span><span className="text-ink-900 font-medium">I need a Non-VAT Official Receipt (OR)</span>
                <span className="block text-ink-500 text-xs mt-0.5">Adds a 3% percentage tax ({peso(data.or_option.or_amount)}) to your total. Required only if you need an OR for your business.</span></span>
            </label>
          )}
          <MethodPicker data={data} method={method} setMethod={setMethod} recurring={recurring} />
          {method && (
            <>
              <Breakdown data={eff} isCard={isCard} total={total} recurring={recurring} />
              {isCard ? (
                <PayPalArea data={eff} token={token} recurring={recurring} buyerBody={buyerBody} finish={finish} />
              ) : (
                <QrArea data={eff} token={token} bankKey={method} total={total} buyerBody={buyerBody} finish={finish} />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function MethodPicker({ data, method, setMethod, recurring }) {
  const bankEntries = Object.entries(data.banks || {});
  return (
    <div data-testid="paylink-method-picker">
      <div className="eyebrow mb-2">How would you like to pay?</div>
      <div className="space-y-2">
        <button onClick={() => setMethod("card")} data-testid="paylink-method-card"
          className={`w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${method === "card" ? "border-brand-600 bg-brand-50" : "border-line hover:border-brand-600/40"}`}>
          <CreditCard className="h-5 w-5 text-brand-700 shrink-0" />
          <div className="flex-1">
            <div className="text-sm text-ink-900">Credit / Debit Card{recurring ? "" : " or PayPal"}</div>
            <div className="text-2xs text-ink-400">Processing fee applies</div>
          </div>
          {method === "card" && <Check className="h-4 w-4 text-brand-700" />}
        </button>
        {!recurring && bankEntries.map(([k, b]) => (
          <button key={k} onClick={() => setMethod(k)} data-testid={`paylink-method-${k}`}
            className={`w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${method === k ? "border-brand-600 bg-brand-50" : "border-line hover:border-brand-600/40"}`}>
            <Landmark className="h-5 w-5 text-brand-700 shrink-0" />
            <div className="flex-1">
              <div className="text-sm text-ink-900">{b.name}</div>
              <div className="text-2xs text-ink-400">Bank / e-wallet QR transfer</div>
            </div>
            <span className="text-2xs font-medium text-positive bg-positive/10 rounded-full px-2 py-0.5">No extra fee</span>
            {method === k && <Check className="h-4 w-4 text-brand-700 ml-1" />}
          </button>
        ))}
      </div>
      {recurring && <p className="mt-2 text-2xs text-ink-400">Subscriptions are billed automatically via card/PayPal.</p>}
    </div>
  );
}

function Breakdown({ data, isCard, total, recurring }) {
  const pct = Math.round((data.paypal_pct || 0.034) * 1000) / 10;
  return (
    <div className="rounded-lg border border-line bg-surface-sunk p-4 text-sm" data-testid="paylink-breakdown">
      <Row k="Subtotal" v={peso(data.base_php)} />
      {data.or_amount > 0 && <Row k="OR / Non-VAT (3%)" v={peso(data.or_amount)} />}
      {isCard && !recurring && <Row k={`Card processing fee (${pct}% + ${peso(data.paypal_fixed || 15)})`} v={peso(data.paypal_fee)} />}
      <div className="border-t border-line mt-2 pt-2">
        <Row k={<span className="font-semibold text-ink-900">Total {recurring ? "per " + (data.interval === "YEAR" ? "year" : "month") : "due"}</span>}
          v={<span className="font-semibold text-ink-900" data-testid="paylink-total">{peso(total)}{recurring ? (data.interval === "YEAR" ? " / yr" : " / mo") : ""}</span>} />
      </div>
      {isCard ? (
        <p className="mt-2 text-2xs text-ink-400" data-testid="paylink-fee-disclaimer">A {pct}% + {peso(data.paypal_fixed || 15)} payment processing fee is added to card / PayPal payments to cover PayPal's charges. Choose a bank / e-wallet transfer to avoid this fee.</p>
      ) : (
        <p className="mt-2 text-2xs text-positive" data-testid="paylink-nofee-note">No processing fee for bank / e-wallet transfers.</p>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex items-center justify-between py-0.5"><span className="text-ink-500">{k}</span><span className="text-ink-800">{v}</span></div>;
}

function PayPalArea({ data, token, recurring, buyerBody, finish }) {
  const ref = useRef(null);
  const guardRef = useRef(false);
  const detailRef = useRef("");
  const [state, setState] = useState("loading");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    const cid = data.paypal.client_id;
    if (!cid) { setState("error"); setMsg("PayPal not configured."); return; }
    let buttons; let closed = false;
    const loader = recurring ? loadSub : loadCapture;
    loader(cid).then((paypal) => {
      if (closed || !ref.current) return;
      ref.current.replaceChildren();
      const cfg = recurring ? {
        style: { shape: "pill", layout: "vertical", label: "subscribe", height: 44 },
        createSubscription: (d, actions) => actions.subscription.create({ plan_id: data.paypal.plan_id }),
        onApprove: async (d) => {
          setState("loading"); setMsg("Confirming subscription…");
          try { finish(await post(`/pay/${token}/subscription/confirm`, { subscription_id: d.subscriptionID, ...buyerBody() })); }
          catch (e) { setState("error"); setMsg(e?.response?.data?.detail || "Could not confirm the subscription."); }
        },
        onError: () => { if (!closed) { setState("error"); setMsg("PayPal reported an error."); } },
      } : {
        style: { shape: "pill", layout: "vertical", label: "pay", height: 44 },
        createOrder: async () => {
          detailRef.current = "";
          try { const r = await post(`/pay/${token}/create-order`, { ...buyerBody() }); return r.paypal_order_id; }
          catch (e) { detailRef.current = e?.response?.data?.detail || "Could not start PayPal checkout."; guardRef.current = true; throw e; }
        },
        onApprove: async (d) => {
          setState("loading"); setMsg("Confirming payment…");
          try { finish(await post(`/pay/${token}/capture-order`, { paypal_order_id: d.orderID, ...buyerBody() })); }
          catch (e) { setState("error"); setMsg(e?.response?.data?.detail || "Payment wasn't completed."); }
        },
        onError: () => { if (closed) return; if (guardRef.current) { guardRef.current = false; } setState("error"); setMsg(detailRef.current || "PayPal reported an error."); detailRef.current = ""; },
      };
      buttons = paypal.Buttons(cfg);
      if (buttons.isEligible && !buttons.isEligible()) { setState("error"); setMsg("PayPal isn't available."); return; }
      return buttons.render(ref.current).then(() => { if (!closed) setState("ready"); })
        .catch(() => { if (!closed) { setState("error"); setMsg("PayPal couldn't load."); } });
    }).catch(() => { if (!closed) { setState("error"); setMsg("PayPal couldn't load."); } });
    return () => { closed = true; try { buttons && buttons.close(); } catch (e) { /* noop */ } };
  }, [recurring]); // eslint-disable-line

  return (
    <div>
      <div ref={ref} className="min-h-[48px]" data-testid="paylink-buttons" />
      {state === "loading" && <p className="text-xs text-ink-400 mt-1 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />{msg || "Loading PayPal…"}</p>}
      {state === "error" && <p className="text-xs text-signal mt-1" data-testid="paylink-pay-error">{msg}</p>}
    </div>
  );
}

function QrArea({ data, token, bankKey, total, buyerBody, finish }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [zoom, setZoom] = useState(false);
  const [copied, setCopied] = useState(false);
  const bank = data.banks[bankKey] || {};
  const qrFilename = `bloomconnect-${bankKey}-qr${(bank.image || "").match(/\.(png|jpe?g|webp)$/i)?.[0] || ".png"}`;
  const detailsText = `${bank.name}\nAccount name: ${bank.holder}\nReference: ${bank.ref}\nPay to: ${data.payee}\nAmount: ${peso(total)}`;
  const copyDetails = async () => {
    try { await navigator.clipboard.writeText(detailsText); }
    catch { window.prompt("Copy the payment details:", detailsText); }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const submit = async () => {
    if (!file) { setMsg("Please attach a screenshot of your payment."); return; }
    setBusy(true); setMsg("");
    try {
      const reader = new FileReader();
      const b64 = await new Promise((res, rej) => { reader.onload = () => res(reader.result); reader.onerror = rej; reader.readAsDataURL(file); });
      finish({ ...(await post(`/pay/${token}/proof`, { payment_method: bankKey, data_base64: b64, content_type: file.type, filename: file.name, ...buyerBody() })), needs_verification: true });
    } catch (e) { setMsg(e?.response?.data?.detail || "Couldn't upload your proof. Please retry."); }
    finally { setBusy(false); }
  };

  const downloadQr = async () => {
    try {
      const resp = await fetch(bank.image, { cache: "no-store" });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = qrFilename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      window.open(bank.image, "_blank", "noopener");
    }
  };

  return (
    <div data-testid="paylink-qr">
      <div className="rounded-lg border border-line p-4 text-center">
        <button type="button" onClick={() => setZoom(true)} className="block mx-auto group relative" data-testid="paylink-qr-enlarge-img" aria-label="Enlarge QR code">
          <img src={bank.image} alt={`${bank.name} QR code`} className="h-44 w-44 object-contain mx-auto transition-transform group-hover:scale-[1.03]" onError={(e) => { e.target.style.display = "none"; }} />
          <span className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity bg-ink-950/5 rounded-md">
            <Maximize2 className="h-6 w-6 text-brand-700" />
          </span>
        </button>
        <div className="mt-3 flex items-center justify-center flex-wrap gap-2">
          <button type="button" onClick={() => setZoom(true)} className="btn-secondary btn-sm inline-flex items-center gap-1.5" data-testid="paylink-qr-enlarge">
            <Maximize2 className="h-3.5 w-3.5" /> Enlarge
          </button>
          <button type="button" onClick={downloadQr} className="btn-secondary btn-sm inline-flex items-center gap-1.5" data-testid="paylink-qr-download">
            <Download className="h-3.5 w-3.5" /> Download QR
          </button>
        </div>
        <p className="mt-3 text-sm text-ink-700">Send <b>{peso(total)}</b> to <b>{data.payee}</b></p>
        <p className="text-xs text-ink-400">{bank.name} · {bank.holder}{bank.ref ? ` · ${bank.ref}` : ""}</p>
        <button type="button" onClick={copyDetails} className="mt-2 btn-secondary btn-sm inline-flex items-center gap-1.5" data-testid="paylink-qr-copy-details">
          {copied ? <><Check className="h-3.5 w-3.5 text-positive" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy account details</>}
        </button>
      </div>
      <label className="block mt-3 text-sm">
        <span className="eyebrow block mb-1">Upload payment screenshot</span>
        <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="paylink-proof-input" />
      </label>
      {msg && <p className="text-xs text-signal mt-2" data-testid="paylink-qr-error">{msg}</p>}
      <button onClick={submit} disabled={busy} className="btn-primary btn-md w-full mt-3" data-testid="paylink-proof-submit">{busy ? "Uploading…" : "I've paid — submit proof"}</button>

      {zoom && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" data-testid="paylink-qr-lightbox" onClick={() => setZoom(false)}>
          <div className="absolute inset-0 bg-ink-950/70 backdrop-blur-sm" />
          <div className="relative bg-white rounded-xl p-5 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setZoom(false)} className="absolute top-3 right-3 text-ink-400 hover:text-ink-700" data-testid="paylink-qr-lightbox-close" aria-label="Close"><X className="h-5 w-5" /></button>
            <div className="eyebrow text-brand-700">{bank.name}</div>
            <img src={bank.image} alt={`${bank.name} QR code`} className="mt-3 w-full max-w-[320px] mx-auto object-contain" />
            <p className="mt-3 text-sm text-ink-700">Send <b>{peso(total)}</b> to <b>{data.payee}</b></p>
            <p className="text-xs text-ink-400">{bank.holder}{bank.ref ? ` · ${bank.ref}` : ""}</p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <button type="button" onClick={downloadQr} className="btn-primary btn-sm inline-flex items-center gap-1.5" data-testid="paylink-qr-lightbox-download">
                <Download className="h-3.5 w-3.5" /> Download QR
              </button>
              <button type="button" onClick={copyDetails} className="btn-secondary btn-sm inline-flex items-center gap-1.5" data-testid="paylink-qr-lightbox-copy">
                {copied ? <><Check className="h-3.5 w-3.5 text-positive" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy details</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
